package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// UC-020 for one opened document beside the list. The server decides visibility, editable fields
// and permitted actions; showing a control grants nothing. State lives only as long as this
// document of this organisation is shown.
@Composable
internal fun documentProcessing(
    organisationId: String,
    documentId: String,
    filter: DocumentFilter,
    changed: () -> Unit,
    lost: (OnboardingProblem) -> Unit,
) {
    val port = LocalDocumentWorkflowPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, documentId) {
            DocumentWorkflowState(port, scope, organisationId, documentId, changed, lost)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    documentCard(organisationId, filter, model.state, model.actions)
}

@Composable
internal fun documentCard(
    organisationId: String,
    filter: DocumentFilter,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    val detail = state.detail
    Section(attrs = CardStyle.toAttrs { attr("aria-labelledby", DETAIL_TITLE_ID) }) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            Div(attrs = RowStyle.toAttrs()) {
                // A file name may have no break opportunity at 360 px; PageStyle wraps it anywhere.
                H2(
                    attrs = {
                        id(DETAIL_TITLE_ID)
                        attr("tabindex", "-1")
                    }
                ) {
                    Text(detail?.fileName ?: "Dokument")
                }
                detail?.let { badge(documentStatusLabel(it.status), statusTone(it.status)) }
            }
        }
        Div(attrs = CardBodyStyle.toAttrs()) {
            Div(attrs = StackStyle.toAttrs()) { processingBody(organisationId, state, actions) }
        }
        Div(attrs = CardFootStyle.toAttrs()) {
            linkButton(
                if (filter == DocumentFilter.INBOX) "Zur Inbox" else "Zur Liste",
                documentsHref(organisationId, filter),
                ButtonVariant.GHOST,
            )
        }
    }
}

@Composable
private fun processingBody(
    organisationId: String,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    processingFocus(state)
    processingProblem(state.error)
    if (state.busy && !state.removed)
        P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
            Text(if (state.detail == null) "Dokument wird geladen …" else "Wird verarbeitet …")
        }
    state.notice?.let { P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(it) } }
    val detail = state.detail
    if (detail == null) {
        if (state.error != null && !state.busy) reloadButton(state, actions)
        return
    }
    // Step 3: the source, saved details and classification. No extraction suggestions (B4).
    facts(detail.fileName, detail.mediaType, detail.sizeBytes, detail.uploadedAt)
    sourceLinks(organisationId, detail)
    detailsForm(state, actions)
    processingActions(organisationId, detail, state, actions)
    documentHistory(detail, state)
    reloadButton(state, actions)
}

// Steps 7-10, A6-A12: one confirmation at a time replaces the action buttons.
@Composable
private fun processingActions(
    organisationId: String,
    detail: DocumentDetailResponse,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    H3(attrs = LabelStyle.toAttrs()) { Text("Bearbeitung") }
    paymentSummary(detail)
    val confirmation = state.confirmation
    if (confirmation != null) {
        documentConfirmation(detail, confirmation, state, actions)
        return
    }
    val disabled = state.busy
    if (detail.transitions.isEmpty())
        P(attrs = HintStyle.toAttrs()) { Text("Für dich ist kein Statuswechsel möglich.") }
    else
        Div(attrs = RowStyle.toAttrs { attr("aria-label", "Statuswechsel") }) {
            detail.transitions.forEach { target ->
                themedButton(
                    transitionLabel(detail.status, target),
                    { actions.transition(target) },
                    variant =
                        if (target == REJECTED) ButtonVariant.DANGER else ButtonVariant.PRIMARY,
                    disabled = disabled,
                )
            }
        }
    if (state.dirty && detail.transitions.isNotEmpty())
        P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
            Text(
                "Nicht gespeicherte Änderungen werden mit dem Statuswechsel gespeichert, sobald du " +
                    "ihn bestätigst."
            )
        }
    paymentAndRemovalActions(detail, state, actions)
    retention(detail)
    if (detail.status == ARCHIVED || detail.status == REJECTED)
        P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
            Text("Dieses Dokument ist schreibgeschützt, bis es reaktiviert wird.")
        }
    listLink(organisationId, detail)
}

@Composable
private fun paymentAndRemovalActions(
    detail: DocumentDetailResponse,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    val paymentDisabled = state.busy || state.dirty
    Div(attrs = RowStyle.toAttrs()) {
        if (RECORD_PAYMENT in detail.actions)
            themedButton("Zahlung erfassen", actions.payment, disabled = paymentDisabled)
        if (CORRECT_PAYMENT in detail.actions) {
            themedButton("Zahlung korrigieren", actions.payment, disabled = paymentDisabled)
            themedButton(
                "Zahlung entfernen",
                actions.clearPayment,
                disabled = paymentDisabled,
            )
        }
        if (REMOVE in detail.actions)
            themedButton(
                "Endgültig löschen …",
                actions.remove,
                variant = ButtonVariant.DANGER,
                disabled = state.busy,
            )
    }
    if (state.dirty && (RECORD_PAYMENT in detail.actions || CORRECT_PAYMENT in detail.actions))
        P(attrs = HintStyle.toAttrs()) {
            Text("Speichere oder verwirf die Änderungen, bevor du die Zahlung bearbeitest.")
        }
}

// Shows the saved state again; unsaved edits are discarded.
@Composable
private fun reloadButton(state: DocumentWorkflowUiState, actions: DocumentWorkflowActions) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(
            "Dokument neu laden",
            actions.refresh,
            variant = ButtonVariant.GHOST,
            size = ButtonSize.SMALL,
            disabled = state.busy || state.confirmation != null,
        )
    }
}

@Composable
private fun processingProblem(problem: OnboardingProblem?) {
    if (problem == null) return
    P(
        attrs =
            AlertStyle.toAttrs {
                id(PROCESSING_ERROR_ID)
                attr("role", "alert")
                attr("tabindex", "-1")
            }
    ) {
        Text(problem.message)
    }
    LaunchedEffect(problem) {
        if (AppGlobals.isExporting) return@LaunchedEffect
        // A marked input takes focus so it can be corrected directly.
        val target = problem.fields.firstOrNull() ?: PROCESSING_ERROR_ID
        (document.getElementById(target) as? HTMLElement)?.focus()
    }
}

// After a confirmation closes (recorded, cancelled or refused) the document title takes focus; the
// first render leaves focus with the page title. A shown problem focuses itself afterwards.
@Composable
private fun processingFocus(state: DocumentWorkflowUiState) {
    val focusReturn = remember { FocusReturn() }
    val open = state.confirmation != null
    LaunchedEffect(open) {
        if (focusReturn.pending && !open && !AppGlobals.isExporting)
            (document.getElementById(DETAIL_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

private fun statusTone(status: String) =
    when (status) {
        ARCHIVED -> BadgeTone.HOURS
        REJECTED -> BadgeTone.DANGER
        ACCOUNTING -> BadgeTone.ACCENT
        REVIEW -> BadgeTone.WARNING
        else -> BadgeTone.PRIMARY
    }

internal const val RECORD_PAYMENT = "RECORD_PAYMENT"
internal const val CORRECT_PAYMENT = "CORRECT_PAYMENT"
internal const val REMOVE = "REMOVE"
internal const val SAVE_DETAILS = "SAVE_DETAILS"
internal const val DETAIL_TITLE_ID = "document-detail-title"
internal const val PROCESSING_ERROR_ID = "document-processing-error"
